/**
 * Default Calendar Screen
 *
 * Lets the user choose which calendar new events should default to.
 * Lists writable device calendars and persists the selection via
 * DefaultCalendarManager. An explicit "No default" option resets the
 * preference so creation falls back to the first writable calendar.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Stack } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Colors, ModernPalette } from '@/constants/theme';
import { BorderRadius, Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useDefaultCalendarManager } from '@/hooks/use-scoped-managers';
import {
  DeviceCalendarService,
  type DeviceCalendar,
} from '@/lib/calendar/DeviceCalendarService';

export default function DefaultCalendarScreen() {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const manager = useDefaultCalendarManager();

  const [writableCalendars, setWritableCalendars] = useState<DeviceCalendar[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const service = new DeviceCalendarService();
      const permission = await service.checkPermissions();
      if (permission.status !== 'granted') {
        const result = await service.requestPermissions();
        if (result.status !== 'granted') {
          if (!cancelled) setIsLoading(false);
          return;
        }
      }

      const allCalendars = await service.getDeviceCalendars();
      const writable = allCalendars.filter((cal) => cal.allowsModifications);
      const storedId = manager ? await manager.getStoredId() : null;

      if (cancelled) return;

      setWritableCalendars(writable);
      setSelectedId(storedId);
      setIsLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [manager]);

  const handleSelect = useCallback(
    async (calendarId: string | null) => {
      if (!manager) return;

      setSelectedId(calendarId);
      if (calendarId === null) {
        await manager.clearDefault();
      } else {
        await manager.setDefault(calendarId);
      }
    },
    [manager],
  );

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: palette.background }]}
      edges={['bottom']}
    >
      <Stack.Screen options={{ title: 'Default Calendar', headerShown: true }} />

      {isLoading ? (
        <ActivityIndicator
          size="large"
          color={palette.tint}
          style={styles.loader}
        />
      ) : (
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
        >
          <Text
            style={[
              styles.sectionHint,
              { color: palette.textMuted, fontFamily: getBodyFont() },
            ]}
          >
            New events you create will be added to this calendar by default.
            You can still change the calendar when creating an event.
          </Text>

          <View style={styles.section}>
            <CalendarOption
              label="No default (use first writable)"
              isSelected={selectedId === null}
              onPress={() => handleSelect(null)}
              palette={palette}
              isFirst
              isLast={writableCalendars.length === 0}
            />

            {writableCalendars.map((cal, index) => (
              <CalendarOption
                key={cal.id}
                label={cal.title}
                sublabel={cal.source.name}
                color={cal.color}
                isSelected={selectedId === cal.id}
                onPress={() => handleSelect(cal.id)}
                palette={palette}
                isLast={index === writableCalendars.length - 1}
              />
            ))}
          </View>

          {writableCalendars.length === 0 && (
            <Text
              style={[
                styles.emptyHint,
                { color: palette.textMuted, fontFamily: getBodyFont() },
              ]}
            >
              No writable calendars found on this device.
            </Text>
          )}
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

type CalendarOptionProps = {
  label: string;
  sublabel?: string;
  color?: string;
  isSelected: boolean;
  onPress: () => void;
  palette: typeof Colors.light;
  isFirst?: boolean;
  isLast?: boolean;
};

function CalendarOption({
  label,
  sublabel,
  color,
  isSelected,
  onPress,
  palette,
  isFirst,
  isLast,
}: CalendarOptionProps) {
  const colorScheme = useColorScheme();
  const backgroundColor =
    colorScheme === 'light' ? ModernPalette.white : palette.backgroundLight;

  return (
    <TouchableOpacity
      style={[
        styles.row,
        { backgroundColor },
        isFirst && styles.rowFirst,
        isLast && styles.rowLast,
        !isLast && styles.rowWithBorder,
      ]}
      onPress={onPress}
      activeOpacity={0.6}
    >
      {color && <View style={[styles.colorDot, { backgroundColor: color }]} />}
      <View style={styles.rowTextContainer}>
        <Text
          style={[
            styles.rowLabel,
            { color: palette.text, fontFamily: getBodyFont() },
          ]}
        >
          {label}
        </Text>
        {sublabel && (
          <Text
            style={[
              styles.rowSublabel,
              { color: palette.textMuted, fontFamily: getBodyFont() },
            ]}
          >
            {sublabel}
          </Text>
        )}
      </View>
      {isSelected && (
        <Ionicons name="checkmark" size={22} color={palette.tint} />
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loader: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: Spacing.lg,
  },
  sectionHint: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: Spacing.md,
  },
  section: {
    borderRadius: BorderRadius.md,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: Spacing.md + Spacing.xxs,
    paddingHorizontal: Spacing.lg,
    minHeight: 52,
  },
  rowFirst: {
    borderTopLeftRadius: BorderRadius.md,
    borderTopRightRadius: BorderRadius.md,
  },
  rowLast: {
    borderBottomLeftRadius: BorderRadius.md,
    borderBottomRightRadius: BorderRadius.md,
  },
  rowWithBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ModernPalette.borderLight,
  },
  colorDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: Spacing.md,
  },
  rowTextContainer: {
    flex: 1,
  },
  rowLabel: {
    fontSize: 14,
  },
  rowSublabel: {
    fontSize: 12,
    marginTop: 2,
  },
  emptyHint: {
    fontSize: 13,
    textAlign: 'center',
    marginTop: Spacing.lg,
  },
});
